import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslations } from '@/shared/i18n';
import { useTelegramBackButton } from '@/shared/telegram';

/** Дальше увеличивать нечего: снимки в диалоге не настолько крупные. */
const MAX_SCALE = 4;
/** Двойное нажатие приближает сразу к этому, а не по шагу. */
const DOUBLE_TAP_SCALE = 2.5;
/** Два нажатия подряд считаются двойным, если уложились в этот срок. */
const DOUBLE_TAP_MS = 300;
/** Сдвиг больше этого — это перетаскивание, а не нажатие. */
const TAP_TOLERANCE_PX = 8;
/**
 * Насколько быстро приближает колесо и щипок на тачпаде.
 *
 * Тачпад шлёт события чаще и мельче мыши, поэтому шаг считается от величины
 * прокрутки, а не фиксированный: иначе одно и то же движение давало бы у мыши
 * рывок, а у тачпада — почти ничего.
 */
const WHEEL_SENSITIVITY = 0.0035;

/** Признак нашей записи в истории: по нему видно, что «назад» закроет снимок. */
interface PhotoHistoryState {
  photoViewer?: boolean;
}

/**
 * Полноэкранный просмотр фотографии.
 *
 * Открывается поверх диалога, а не вместо него: лента остаётся смонтированной,
 * и после закрытия человек оказывается ровно там, где читал, — без возврата к
 * последнему сообщению.
 */
export function PhotoViewer({
  url,
  onClose,
}: {
  url: string;
  onClose: () => void;
}): React.ReactNode {
  const dictionary = useTranslations();

  /*
    Обработчик держится в ссылке, а подписки ставятся один раз. Иначе новая
    функция на каждом рендере родителя перезапускала бы весь эффект — а любое
    сообщение в диалоге как раз и вызывает такой рендер.
  */
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    /*
      Своя запись в истории. Жест «назад» — привычный способ закрыть картинку на
      Android и в Telegram; без записи он закрыл бы всё приложение.
    */
    window.history.pushState({ photoViewer: true } satisfies PhotoHistoryState, '');

    const onPopState = (): void => close.current();
    window.addEventListener('popstate', onPopState);

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        requestClose();
      }
    };
    document.addEventListener('keydown', onKey);

    // В мини-приложении «назад» — это кнопка клиента, а не жест браузера.
    const releaseBackButton = useTelegramBackButton(() => requestClose());

    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('keydown', onKey);
      releaseBackButton();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    Закрытие всегда идёт через историю: и крестик, и тап по фону, и жест «назад»
    оставляют её в одном состоянии. Возврат сам вызовет закрытие — отдельно
    трогать состояние не нужно, иначе оно менялось бы дважды.
  */
  const requestClose = (): void => {
    if ((window.history.state as PhotoHistoryState | null)?.photoViewer) {
      window.history.back();
      return;
    }
    close.current();
  };

  /*
    Своё увеличение, а не браузерное.

    Страница объявлена немасштабируемой, но iOS этого не слушает: снимок
    увеличивался вместе со всей страницей, а приложение в это время
    пересчитывало высоту по видимой части окна — и картинка дёргалась под
    пальцами. Здесь жест обрабатывается сам, а браузеру он не достаётся вовсе
    (`touch-action: none`), поэтому дёргаться нечему.

    Состояние держится в ссылках, а не в состоянии React: перерисовка на каждое
    движение пальца — это и есть рывки, которых мы избегаем. Снимку меняется
    только `transform`.
  */
  const imageRef = useRef<HTMLImageElement>(null);
  const view = useRef({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  /** Расстояние и середина между пальцами в начале щипка. */
  const pinch = useRef<{ distance: number; x: number; y: number } | null>(null);
  /** Начало одиночного касания: по нему отличается нажатие от перетаскивания. */
  const press = useRef<{ x: number; y: number; at: number; moved: boolean } | null>(null);
  const lastTap = useRef(0);

  const apply = (): void => {
    const node = imageRef.current;
    if (!node) {
      return;
    }

    /*
      Границы сдвига. Увеличенный снимок можно возить, но не утаскивать за край:
      иначе человек теряет картинку и не понимает, куда она делась.
    */
    const { scale } = view.current;
    const limitX = (node.clientWidth * (scale - 1)) / 2;
    const limitY = (node.clientHeight * (scale - 1)) / 2;
    view.current.x = Math.min(limitX, Math.max(-limitX, view.current.x));
    view.current.y = Math.min(limitY, Math.max(-limitY, view.current.y));

    node.style.transform = `translate3d(${view.current.x}px, ${view.current.y}px, 0) scale(${scale})`;
  };

  /**
   * @param smooth плавно — для одиночных действий вроде двойного нажатия. У
   *   колеса и тачпада событий десятки в секунду, и переход на каждом из них
   *   превращает плавность в кашу: там снимок должен идти за движением след
   *   в след.
   */
  const zoomTo = (scale: number, originX: number, originY: number, smooth = true): void => {
    const node = imageRef.current;
    if (!node) {
      return;
    }

    const next = Math.min(MAX_SCALE, Math.max(1, scale));
    const rect = node.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    /*
      Точка под пальцами остаётся на месте: приближение к середине экрана вместо
      места, куда человек показывает, ощущается как чужое.
    */
    const ratio = next / view.current.scale;
    view.current.x = originX - centerX - (originX - centerX - view.current.x) * ratio;
    view.current.y = originY - centerY - (originY - centerY - view.current.y) * ratio;
    view.current.scale = next;

    if (next === 1) {
      view.current.x = 0;
      view.current.y = 0;
    }

    if (!smooth) {
      apply();
      return;
    }

    node.style.transition = 'transform 160ms ease-out';
    apply();
    window.setTimeout(() => {
      if (imageRef.current) {
        imageRef.current.style.transition = '';
      }
    }, 170);
  };

  /*
    Колесо и тачпад. На компьютере пальцами не щипают: мышь крутит колесо, а
    щипок на тачпаде браузер присылает тем же событием с зажатым Ctrl. Без
    своей обработки этот жест уходил браузеру, и страница ездила туда-сюда.

    Слушатель ставится вручную, а не пропсом: React вешает колесо пассивным, а
    пассивному нельзя отменить действие по умолчанию — то самое, из-за которого
    всё и ездит.
  */
  useEffect(() => {
    const node = imageRef.current;
    if (!node) {
      return;
    }

    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * WHEEL_SENSITIVITY);
      zoomTo(view.current.scale * factor, event.clientX, event.clientY, false);
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLImageElement>): void => {
    /*
      Захват указателя удерживает события за снимком, даже если палец ушёл за
      его край. Не всякий указатель можно захватить — например, событие,
      порождённое скриптом, — и отказ здесь ничего не значит: без захвата жест
      просто оборвётся на краю.
    */
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Захват недоступен — работаем без него.
    }

    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      const [one, other] = [...pointers.current.values()];
      pinch.current = {
        distance: Math.hypot(one!.x - other!.x, one!.y - other!.y),
        x: (one!.x + other!.x) / 2,
        y: (one!.y + other!.y) / 2,
      };
      press.current = null;
      return;
    }

    press.current = { x: event.clientX, y: event.clientY, at: Date.now(), moved: false };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLImageElement>): void => {
    if (!pointers.current.has(event.pointerId)) {
      return;
    }

    const previous = pointers.current.get(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2 && pinch.current) {
      const [one, other] = [...pointers.current.values()];
      const distance = Math.hypot(one!.x - other!.x, one!.y - other!.y);
      const midX = (one!.x + other!.x) / 2;
      const midY = (one!.y + other!.y) / 2;

      if (pinch.current.distance > 0) {
        const scale = view.current.scale * (distance / pinch.current.distance);
        view.current.scale = Math.min(MAX_SCALE, Math.max(1, scale));
        // Середина между пальцами тянет снимок за собой: так щипок можно
        // совмещать с перетаскиванием, не отрывая пальцев.
        view.current.x += midX - pinch.current.x;
        view.current.y += midY - pinch.current.y;
      }

      pinch.current = { distance, x: midX, y: midY };
      apply();
      return;
    }

    if (!previous || view.current.scale === 1) {
      return;
    }

    if (press.current) {
      const shift = Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y);
      if (shift > TAP_TOLERANCE_PX) {
        press.current.moved = true;
      }
    }

    view.current.x += event.clientX - previous.x;
    view.current.y += event.clientY - previous.y;
    apply();
  };

  const onPointerUp = (event: React.PointerEvent<HTMLImageElement>): void => {
    pointers.current.delete(event.pointerId);

    if (pointers.current.size < 2) {
      pinch.current = null;
    }

    // Отпустили после щипка, а увеличения не осталось — возвращаем на место.
    if (pointers.current.size === 0 && view.current.scale <= 1.01) {
      zoomTo(1, 0, 0);
    }

    const started = press.current;
    press.current = null;
    if (!started || started.moved || pointers.current.size > 0) {
      return;
    }

    /*
      Двойное нажатие приближает и возвращает обратно. Одиночное по снимку
      по-прежнему не делает ничего: закрывает нажатие мимо него или крестик —
      так было и раньше, и менять это заодно с увеличением незачем.
    */
    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_MS) {
      lastTap.current = 0;
      zoomTo(view.current.scale > 1 ? 1 : DOUBLE_TAP_SCALE, event.clientX, event.clientY);
      return;
    }

    lastTap.current = now;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95"
      onClick={requestClose}
    >
      <img
        ref={imageRef}
        src={url}
        alt=""
        // Снимок занимает всё доступное место, но не обрезается: в диалоге он
        // показан кадрированным, и открывают его как раз чтобы увидеть целиком.
        className="max-h-full max-w-full touch-none select-none object-contain"
        draggable={false}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      <button
        type="button"
        aria-label={dictionary.common.close}
        onClick={requestClose}
        className="absolute right-3 flex size-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm active:bg-white/25"
        style={{ top: 'calc(0.75rem + var(--safe-top))' }}
      >
        <svg viewBox="0 0 24 24" className="size-6" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </motion.div>
  );
}
